import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { formatNumber } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';

const VALUE_FALLBACK = '—';

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeText(value) {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatNumeric(value) {
    if (value === undefined || value === null || value === '') return VALUE_FALLBACK;
    const n = Number(value);
    if (!Number.isFinite(n)) return VALUE_FALLBACK;
    return formatNumber(n, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatDate(value) {
    return formatPurchaseOrderDate(value, VALUE_FALLBACK);
}

/**
 * @param {Record<string, unknown>} item
 * @param {number} index
 * @returns {string}
 */
function resolveGroupKey(item, index) {
    const lineId = normalizeText(item?.id ?? item?.transferItemId ?? item?.transfer_item_id);
    if (lineId) {
        return `line:${lineId}`;
    }

    const lotId = normalizeText(item?.lotId ?? item?.lot_id);
    const externalSku = normalizeText(item?.externalSku ?? item?.external_sku);
    if (lotId || externalSku) {
        return `lot:${lotId || 'na'}-sku:${externalSku || 'na'}-idx:${index}`;
    }

    return `idx:${index}`;
}

/**
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function resolveItemLabel(item) {
    const itemName = normalizeText(item?.itemName ?? item?.item_name);
    if (itemName) {
        return itemName;
    }

    const sku = normalizeText(item?.externalSku ?? item?.external_sku);
    const lot = normalizeText(item?.lotId ?? item?.lot_id);
    if (sku && lot) {
        return `SKU ${sku} (Lot ${lot})`;
    }
    if (sku) {
        return `SKU ${sku}`;
    }
    if (lot) {
        return `Lot ${lot}`;
    }

    const itemId = normalizeText(item?.id ?? item?.transferItemId ?? item?.transfer_item_id);
    if (itemId) {
        return `Transfer line ${itemId}`;
    }
    return 'Transfer line';
}

/**
 * @param {Record<string, unknown>} discrepancy
 * @returns {string}
 */
function formatDiscrepancyMessage(discrepancy) {
    const type = normalizeText(discrepancy?.discrepancyType ?? discrepancy?.discrepancy_type);
    if (type === 'total_unit_discrepancy') {
        return `Total units mismatch: expected ${formatNumeric(discrepancy?.expectedTotalUnits)}, received ${formatNumeric(discrepancy?.receivedTotalUnits)}.`;
    }
    if (type === 'units_per_case_discrepancy') {
        return `Units per case mismatch: expected ${formatNumeric(discrepancy?.expectedUnitsPerCase)}, received ${formatNumeric(discrepancy?.receivedUnitsPerCase)}.`;
    }
    if (type === 'expiration_date_discrepancy') {
        return `Expiration date mismatch: expected ${formatDate(discrepancy?.expectedExpiresOn)}, received ${formatDate(discrepancy?.receivedExpiresOn)}.`;
    }
    return 'Mismatch detected for this transfer line.';
}

/**
 * @param {Record<string, unknown> | null | undefined} transfer
 * @returns {Array<{ groupKey: string, itemLabel: string, messages: string[] }>}
 */
export function getFCTransferDiscrepancyGroups(transfer) {
    const items = Array.isArray(transfer?.items) ? transfer.items : [];
    const groups = [];
    for (const [index, rawItem] of items.entries()) {
        if (rawItem && typeof rawItem === 'object') {
            const item = /** @type {Record<string, unknown>} */ (rawItem);
            const discrepancies = Array.isArray(item?.discrepancies) ? item.discrepancies : [];

            const messages = [];
            for (const rawDiscrepancy of discrepancies) {
                if (rawDiscrepancy && typeof rawDiscrepancy === 'object') {
                    const discrepancy = /** @type {Record<string, unknown>} */ (rawDiscrepancy);
                    messages.push(formatDiscrepancyMessage(discrepancy));
                }
            }

            if (messages.length > 0) {
                groups.push({
                    groupKey: resolveGroupKey(item, index),
                    itemLabel: resolveItemLabel(item),
                    messages,
                });
            }
        }
    }
    return groups;
}
